import React, { useState, useRef, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Transition } from '@headlessui/react'; // Import Menu and Transition
import { LogIn, PlusCircle, MoreVertical, Save, ChevronRight, SquareCheckBig, ArrowRight, FileChartColumn, Package, Globe, Settings2, LucideIcon, LogOut, Sun, Moon, Pencil, Download, Upload } from 'lucide-react'; // Import Download and Upload
import { HomeGraph, Monitoring, SchemaDiagram, Pipelines, Destinations, User03, BookOpen01 } from './icons';
import clsx from 'clsx';
import AuthRequiredModal from "./dialogs/AuthRequiredModal";
import KeyboardShortcutsDialog from "./dialogs/KeyboardShortcutsDialog";
import RenameSchemaDialog from "./dialogs/RenameSchemaDialog"; // Import RenameSchemaDialog
import { useAuth } from "../hooks/useAuth";
import { useUIContext } from "../providers/UIProvider";
import { useClickOutside } from "../hooks/useClickOutside";
import { UnifiedSchema } from "../types/schema"; // Import UnifiedSchema

export interface BreadcrumbItem {
  name: string;
  iconName?: string;
  path?: string;
}

export interface ActionButton {
  label: string;
  onClick?: () => void; // Make onClick optional
  path?: string; // New property for navigation
  iconName?: string;
  variant?: 'primary' | 'secondary' | 'text' | 'destructive';
  hiddenOnMobile?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

export interface TabItem {
  label: string;
  path: string;
  iconName?: string;
}

const iconMap: { [key: string]: LucideIcon | React.FC<React.SVGProps<SVGSVGElement>> } = {
  LogIn: LogIn,
  PlusCircle: PlusCircle,
  MoreVertical: MoreVertical,
  SquareCheckBig: SquareCheckBig,
  Save: Save,
  ChevronRight: ChevronRight,
  ArrowRight: ArrowRight,
  Package: Package,
  FileChartColumn: FileChartColumn,
  Settings: Settings2,
  BookOpen01: BookOpen01,
  HomeGraph: HomeGraph,
  Globe: Globe,
  Monitoring: Monitoring,
  SchemaDiagram: SchemaDiagram,
  Pipelines: Pipelines,
  Destinations: Destinations,
  Download: Download,
  Upload: Upload,
};

interface LabsAppBarProps {
  breadcrumbs: BreadcrumbItem[];
  actions?: ActionButton[];
  tabs?: TabItem[];
  currentSchema?: UnifiedSchema | null; // New prop for current schema
  onSchemaRenameConfirm?: (newName: string, newDescription: string) => Promise<void>;
  variant?: 'default' | 'stepper' | 'progress'; // New prop for variant
  steps?: { id: number; title: string }[];
  currentStep?: number;
  onStepClick?: (stepId: number) => void;
  progress?: { current: number; total: number; label: string }; // New prop for progress bar
  isSmallScreen?: boolean; // Add the new prop
  onExport?: (action: 'playbook-yaml' | 'connection-yaml' | 'combined-gz' | 'combined-yaml') => void; // New prop for export functionality
  selectedTabPath?: string; // Add this prop
}

const LabsAppBar: React.FC<LabsAppBarProps> = ({ breadcrumbs, actions, tabs, currentSchema, onSchemaRenameConfirm, variant = 'default', steps, currentStep, onStepClick, progress, isSmallScreen, onExport, selectedTabPath }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [showRenameSchemaDialog, setShowRenameSchemaDialog] = useState(false);
  const { logout, isAuthenticated, user } = useAuth();
  const { isDarkMode, toggleDarkMode, toggleDocumentationPanel } = useUIContext();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useClickOutside(
    () => {
      setIsUserMenuOpen(false);
    },
    [],
    [userMenuRef]
  );

  // Helper function to truncate email
  const truncateEmail = (email: string | undefined): string => {
    if (!email) return '';
    
    const [username, domain] = email.split('@');
    if (!domain) return email;
    
    // Show first 4 characters of username + ellipsis + @ + domain
    return username.length > 4
      ? `${username.substring(0, 4)}...@${domain}`
      : email;
  };

  const appBarHeightClass = (tabs && tabs.length > 0 && variant === 'default') ? "h-[56px] pt-2" : "h-[64px] py-2";

  return (
    <div className={clsx("sticky top-0 z-100 bg-white border-b border-gray-200 flex shadow-sm flex-col px-6 md:px-4 sm:px-3 min-w-[800px]", isDarkMode && "dark:bg-gray-800 dark:border-gray-700")}>
      <div className={clsx("flex justify-between items-center w-full", appBarHeightClass)}>
        {/* Left section: Breadcrumbs */}
        <div className="flex items-center gap-1">
          {breadcrumbs.slice(0, 3).map((item, index) => {
            const isLastBreadcrumb = index === breadcrumbs.length - 1;
            const isSchemaEditPage = location.pathname.startsWith('/labs/schema-edit/');
            // Simplified: Assume it's clickable if it's the last breadcrumb on the schema edit page
            // The actual dialog opening will check for currentSchema and onSchemaRenameConfirm
            const shouldBeClickable = isLastBreadcrumb && isSchemaEditPage;

            const content = (
              <>
                {item.iconName && iconMap[item.iconName] && index === 0 && ( // Only show icon for the first breadcrumb
                  <div className={clsx("flex justify-center items-center rounded-full cursor-pointer", index === 0 ? "w-9 h-9 mr-0.5" : "w-5 h-5 mr-1")}>
                    {React.createElement(iconMap[item.iconName], {
                      className: clsx(index === 0 ? "w-5 h-5 text-gray-600" : "w-5 h-5 text-gray-600", isDarkMode && "dark:text-gray-400")
                    })}
                  </div>
                )}
                <span className={clsx(
                  index === 0 ? "text-gray-800 text-lg font-medium leading-7" : "text-gray-900 text-base font-medium",
                  isDarkMode && (index === 0 ? "dark:text-gray-100" : "dark:text-gray-200"),
                  shouldBeClickable && "group-hover:text-gray-600 dark:group-hover:text-gray-400", // Use shouldBeClickable here
                  "truncate min-w-0"
                )}>{shouldBeClickable && currentSchema?.name ? currentSchema.name : item.name}</span> {/* Use shouldBeClickable here */}
                {shouldBeClickable && ( // Use shouldBeClickable here
                  <span className="flex items-center w-0 overflow-hidden group-hover:w-6 transition-all duration-300">
                    <Pencil size={16} className={clsx("ml-2 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400", isDarkMode && "dark:text-gray-500")} />
                  </span>
                )}
              </>
            );

            return (
              <React.Fragment key={item.name}>
                {index > 0 && <span className={clsx("mx-1 text-gray-400", isDarkMode && "dark:text-gray-500")}>/</span>}
                {item.path && !shouldBeClickable ? ( // Use shouldBeClickable here
                  <Link to={item.path} className={clsx("flex items-center text-gray-700 no-underline hover:text-blue-600", isDarkMode && "dark:text-gray-300 dark:hover:text-blue-400")}>
                    {content}
                  </Link>
                ) : (
                  <span
                    className={clsx(
                      "flex items-center text-gray-700",
                      isDarkMode && "dark:text-gray-300",
                      shouldBeClickable && "cursor-pointer rounded-md px-2 py-1 -mx-2 -my-1 group hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    )}
                    onClick={shouldBeClickable ? () => {
                      if (currentSchema && onSchemaRenameConfirm) { // Check here before showing dialog
                        setShowRenameSchemaDialog(true);
                      }
                    } : undefined}
                  >
                    {content}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Stepper section (if variant is 'stepper') */}
        {variant === 'stepper' && steps && currentStep !== undefined && onStepClick && (
          <div className="flex items-center justify-center flex-grow max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl w-full gap-x-2">
            {steps.map((step) => {
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;

              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => onStepClick(step.id)}
                    className={clsx(
                      "flex items-center gap-2 py-2 px-2 rounded-full transition-colors duration-200 focus:ring focus:outline-none dark:focus:ring-blue-500 dark:focus:bg-blue-700 focus:ring-blue-500",
                      isActive
                        ? "bg-blue-500 dark:bg-blue-600 text-white shadow-md"
                        : isCompleted
                          ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900"
                          : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-600/20",
                      "flex-shrink-0"
                    )}
                  >
                    <div className={clsx(
                      "w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold",
                      isActive
                        ? "bg-white text-blue-500"
                        : isCompleted
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200"
                    )}>
                      {isCompleted ? <SquareCheckBig size={14} /> : step.id}
                    </div>
                    {!isSmallScreen && ( // Conditionally hide on small screens
                      <span className={clsx(
                        "text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis mr-2",
                        "hidden sm:inline"
                      )}>
                        {step.title}
                      </span>
                    )}
                  </button>
                  {step.id < steps.length && (
                    <div className={clsx(
                      "w-8 h-0.5 border-t border ml-2",
                      isCompleted ? "border-blue-300 dark:border-blue-500/30" : "border-gray-300 dark:border-gray-600"
                    )} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Progress section (if variant is 'progress') */}
        {variant === 'progress' && progress && (
          <div className="flex items-center justify-center flex-grow max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl w-full px-4">
            <div className="w-full">
              <div className="flex justify-between mb-1">
                <span className={clsx("text-sm font-medium text-gray-700 dark:text-gray-300", isSmallScreen && "hidden sm:inline")}>
                  {progress.label}
                </span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {progress.current} / {progress.total} ({progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <motion.div
                  initial={{ width: '0%' }}
                  animate={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                  className="bg-blue-600 h-2.5 rounded-full"
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Right section: Action Stack and User Menu */}
        <div className="flex items-center gap-4">
          {/* Action Stack (up to 3 buttons + meatball menu) */}
          {actions && actions.length > 0 && (
            <div className="flex items-center gap-2">
              {actions.filter(action => !(variant === 'stepper' && action.label === 'Next')).slice(0, 3).map((action, index) => {
                const ButtonIcon = action.iconName ? iconMap[action.iconName] : undefined;
                const commonClasses = clsx(
                  "flex justify-center items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors h-10",
                  action.variant === 'primary' && "bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg text-white",
                  action.variant === 'secondary' && clsx("border border-blue-300 bg-blue-100 dark:bg-blue-900/30 dark:border-blue-700/50 hover:bg-blue-200 dark:hover:bg-blue-900 shadow hover:shadow-md text-blue-800 dark:text-blue-100"),
                  action.variant === 'destructive' && "bg-red-600 hover:bg-red-700 shadow-md hover:shadow-lg text-white",
                  action.variant === 'text' && clsx("bg-transparent text-gray-700 hover:bg-gray-100", isDarkMode && "dark:text-gray-300 dark:hover:bg-gray-700"),
                  action.hiddenOnMobile && "hidden sm:flex",
                  action.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                );

                if (action.path) {
                  return (
                    <Link
                      key={index}
                      to={action.path}
                      className={clsx(commonClasses, "no-underline")} // Add no-underline for links
                      onClick={action.onClick} // Keep onClick for any additional side effects
                      aria-disabled={action.disabled} // Use aria-disabled for Link
                    >
                      {ButtonIcon && React.createElement(ButtonIcon, { className: "w-5 h-5 flex-shrink-0" })}
                      <span className="text-sm font-bold leading-6 text-center truncate">{action.label}</span>
                    </Link>
                  );
                } else {
                  return (
                    <button
                      key={index}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={commonClasses}
                    >
                      {ButtonIcon && React.createElement(ButtonIcon, { className: "w-5 h-5 flex-shrink-0" })}
                      <span className="text-sm font-bold leading-6 text-center truncate">{action.label}</span>
                    </button>
                  );
                }
              })}
              {(actions.length > 3 || onExport) && (
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="flex w-7 h-7 justify-center items-center rounded-full cursor-pointer sm:flex hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <MoreVertical className={clsx("w-5 h-5 text-gray-600", isDarkMode && "dark:text-gray-400")} />
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {actions.slice(3).map((action, index) => (
                          <Menu.Item key={index}>
                            {({ active }) => {
                              const ButtonIcon = action.iconName ? iconMap[action.iconName] : undefined;
                              return (
                                <button
                                  onClick={action.onClick}
                                  disabled={action.disabled}
                                  className={clsx(
                                    "group flex w-full items-center px-4 py-2 text-sm",
                                    active ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100" : "text-gray-700 dark:text-gray-300",
                                    action.disabled && "opacity-50 cursor-not-allowed"
                                  )}
                                >
                                  {ButtonIcon && React.createElement(ButtonIcon, { className: "mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" })}
                                  {action.label}
                                </button>
                              );
                            }}
                          </Menu.Item>
                        ))}
                        {onExport && (
                          <>
                            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => onExport('connection-yaml')}
                                  className={clsx(
                                    "group flex w-full items-center px-4 py-2 text-sm",
                                    active ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
                                  )}
                                >
                                  <Download className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" />
                                  Export connection.yaml
                                </button>
                              )}
                            </Menu.Item>
                          </>
                        )}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              )}
            </div>
          )}
          <div className='flex gap-2'>
            {/* Documentation Button */}
            <button
              onClick={toggleDocumentationPanel}
              className={clsx(
                "rounded-full h-8 w-8 hover:bg-gray-200 flex items-center justify-center",
                isDarkMode && "dark:hover:bg-gray-700"
              )}
            >
              <BookOpen01 className={clsx("h-5 w-5 text-gray-600", isDarkMode && "dark:text-gray-200")} />
            </button>

            {/* User Avatar and Menu */}
            <div className="relative" ref={userMenuRef}>
              <motion.button
                className="p-0"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                whileHover={{ scale: 1.00 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div 
                  className={clsx("rounded-full h-8 w-8 bg-gray-300 hover:bg-gray-500/40 flex items-center justify-center", isDarkMode && "dark:bg-gray-600 dark:hover:bg-gray-500")}
                  transition={{ duration: 0.2 }}
                >
                  <User03 className={clsx("h-5 w-5 text-gray-600", isDarkMode && "dark:text-gray-200")} />
                </motion.div>
              </motion.button>

              {isUserMenuOpen && (
                <div
                  className={clsx("fixed inset-0 bg-black transition-opacity duration-200 ease-in-out bg-opacity-0 z-40")}
                  onClick={() => setIsUserMenuOpen(false)}
                />
              )}
              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div 
                    className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-900 border border-gray-800 dark:bg-gray-950 ring-1 ring-black ring-opacity-5 z-50"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                  {isAuthenticated && (
                    <div className="px-4 py-2">
                      <div className={clsx("text-xs text-gray-400", isDarkMode && "dark:text-gray-500")}>Logged in as</div>
                      <div className={clsx("text-sm text-gray-200", isDarkMode && "dark:text-gray-300")}>
                        {truncateEmail(user?.email)}
                      </div>
                    </div>
                  )}
                  <div className="py-1" role="menu">
                    {/* Hotkeys - Hidden */}
                    {/* <button
                      onClick={() => setShowKeyboardShortcuts(true)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                      role="menuitem"
                    >
                      <Keyboard className="h-4 w-4 mr-2 inline-block" />
                      Hotkeys
                    </button> */}

                    <button
                      onClick={toggleDarkMode}
                      className={clsx("block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700", isDarkMode && "dark:text-gray-300 dark:hover:bg-gray-700")}
                      role="menuitem"
                    >
                      {isDarkMode ? (
                        <Sun className="h-4 w-4 mr-2 inline-block" />
                      ) : (
                        <Moon className="h-4 w-4 mr-2 inline-block" />
                      )}
                      {isDarkMode ? "Light Mode" : "Dark Mode"}
                    </button>

                    {isAuthenticated ? (
                      <>
                        {/* Admin Panel - Hidden */}
                        {/* {isAdmin && window.location.pathname !== '/admin' && (
                          <Link
                            to="/admin"
                            className={clsx("block px-4 py-2 text-sm text-gray-200 hover:bg-gray-700", isDarkMode && "dark:text-gray-300 dark:hover:bg-gray-700")}
                            role="menuitem"
                          >
                            <PanelLeftOpen className="h-4 w-4 mr-2 inline-block" />
                            Admin Panel
                          </Link>
                        )} */}
                        {/* Labs Page Link - Hidden */}
                        {/* <Link
                          to="/labs"
                          className={clsx("block px-4 py-2 text-sm text-gray-200 hover:bg-gray-700", isDarkMode && "dark:text-gray-300 dark:hover:bg-gray-700")}
                          role="menuitem"
                          onClick={() => setIsUserMenuOpen(false)} // Close menu on click
                        >
                          <img src="/assets/labs.svg" alt="Labs" className="h-4 w-4 mr-2 inline-block" />
                          MetaRouter Labs
                        </Link> */}

                        <button
                          onClick={logout}
                          className={clsx("block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700", isDarkMode && "dark:text-gray-300 dark:hover:bg-gray-700")}
                          role="menuitem"
                        >
                          <LogOut className="h-4 w-4 mr-2 inline-block" />
                          Log out
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          setShowAuthRequiredModal(true);
                        }}
                        className={clsx("block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700", isDarkMode && "dark:text-gray-300 dark:hover:bg-gray-700")}
                        role="menuitem"
                      >
                        <User03 className="h-4 w-4 mr-2 inline-block" />
                        Log in
                      </button>
                    )}

                  </div>
                </motion.div>
              )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Conditional sections based on variant */}
      {tabs && tabs.length > 0 && (
        <div className={clsx("flex px-1 h-[40px] items-end", isDarkMode && "dark:border-gray-700")}>
          {tabs.map((tab) => {
            const isActive = selectedTabPath ? selectedTabPath === tab.path : location.pathname === tab.path;
            const TabIcon = tab.iconName ? iconMap[tab.iconName] : undefined;
            return (
              <motion.div
                key={tab.path}
                className="relative"
              >
                <Link
                  to={tab.path}
                  className={clsx(
                    "flex items-center gap-2 py-3 px-4 text-sm font-medium no-underline relative z-10",
                    isActive
                      ? "text-blue-600 dark:text-blue-400"
                      : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                  )}
                >
                  {TabIcon && React.createElement(TabIcon, { className: "w-4 h-4" })}
                  {tab.label}
                </Link>
                {isActive && (
                  <motion.div
                    layoutId="underline"
                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-600 dark:bg-blue-400"
                    initial={false}
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <AnimatePresence>
        {showAuthRequiredModal && (
          <AuthRequiredModal 
            key="auth-required-modal"
            onClose={() => setShowAuthRequiredModal(false)} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showKeyboardShortcuts && (
          <KeyboardShortcutsDialog 
            key="keyboard-shortcuts-dialog"
            onClose={() => setShowKeyboardShortcuts(false)} 
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showRenameSchemaDialog && currentSchema && onSchemaRenameConfirm && (
          <RenameSchemaDialog
            open={showRenameSchemaDialog}
            onClose={() => setShowRenameSchemaDialog(false)}
            currentSchemaName={currentSchema.name}
            currentSchemaDescription={currentSchema.description}
            onRenameConfirm={onSchemaRenameConfirm}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LabsAppBar;
